import React, { useEffect, useState, useRef } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import styled, { keyframes } from "styled-components";
import { screen } from "app/utils/mixins";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import TwitterIcon from "@mui/icons-material/Twitter";
import { BuildingGear, Building } from "react-bootstrap-icons";
import { selectIsAuthorised, userDocIdSelector } from "app/modules/auth/selectors";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Stack,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MobileHeader from "./MobileHeader";
import MobileSidebar from "./MobileSidebar";
import { Link, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import appRoutes from "app/config/routes";
import { toast } from "react-toastify";
import { useQuery } from "react-query";
import { getSchoolProfile } from "app/services/management";
import DeactivateContent from "./DeactivateContent";
import Policies from "./Policies";
import { setPolicyTimeout } from "app/services/commonSlice";
import ZohoSalesIQ from "app/components/common/ZohoSalesIQ/ZohoSalesIQWidget";
import DueServiceChargePopup from "./DueServiceChargePopup";
import BetaPromoPopup from "./BetaPromoPopup";
import { markBetaPromoShown, shouldShowBetaPromo } from "app/utils/beta";
import { ALLOWED_POPUP_MODULES } from "app/config/constants";
import { updateUpcomingDueDate } from "app/modules/schools/operations";
import { selectSelectedSchoolId } from "app/modules/schools/selectors";

const bottomStripHeight = "25px";

const Layout = ({ children }) => {
  const { policyExpireTime } = useSelector((store) => store.commonStore);
  const [isSidebarToggled, setIsSidebarToggled] = useState(true);
  const [show, setShow] = useState(false);
  const [isPolicyAccepted, setisPolicyAccepted] = useState(true);

  const [showDueChargePopup, setShowDueChargePopup] = useState(false);
  const hasClosedPopupForCurrentPath = useRef(false);

  const [showBetaPromo, setShowBetaPromo] = useState(false);

  const schoolDocId = useSelector(selectSelectedSchoolId);
  const userDocId = useSelector(userDocIdSelector);
  const isAuthenticated = useSelector(selectIsAuthorised);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px)"); // Increased from 992px
  const location = useLocation();
  const dispatch = useDispatch();

  // Auto-hide sidebar on smaller screens
  useEffect(() => {
    if (isTablet) {
      setIsSidebarToggled(false);
    } else {
      setIsSidebarToggled(true);
    }
  }, [isTablet]);

  const handleOnClickHamburgeMenu = () => {
    setIsSidebarToggled(!isSidebarToggled);
  };
  const handleClosePolicyDialog = () => {
    dispatch(setPolicyTimeout(new Date()));
    setisPolicyAccepted(true);
  };

  const handleCloseDueChargePopup = () => {
    hasClosedPopupForCurrentPath.current = true;
    setShowDueChargePopup(false);
  };
  const {
    data: getSchoolProfileData,
    isSuccess: getSchoolProfileDataIsSuccess,
    refetch: getSchoolProfileRefetch,
  } = useQuery({
    queryKey: ["getSchoolProfile"],
    queryFn: () => getSchoolProfile(),
    onSuccess: (success) => {
      const newDueDate = success?.data?.obj?.upcomingDueDate;
      dispatch(
        updateUpcomingDueDate({
          schoolId: schoolDocId,
          upcomingDueDate: newDueDate,
        }),
      );
      let isAccepted = true;
      success?.data?.obj?.legals?.forEach((item) => {
        if (!item?.accepted) {
          isAccepted = false;
          return;
        }
      });
      if (isAccepted) {
        dispatch(setPolicyTimeout(""));
        handleClosePolicyDialog();
      }
    },
    onError: (data) => {
      toast.error(data?.response?.data?.message);
    },
  });

  const dueDate = dayjs(getSchoolProfileData?.data?.obj?.upcomingDueDate);
  const currentDate = dayjs(new Date());

  // Check if current route is settings page
  const isSettingsPage = location.pathname.startsWith("/settings");
  const isSupportBulkUpdatePage = location.pathname.startsWith(
    `/${appRoutes.supportBulkStudentUpdate}`
  );
  const isMinimalLayoutPage = isSettingsPage || isSupportBulkUpdatePage;

  useEffect(() => {
    if (getSchoolProfileDataIsSuccess) {
      let isAccepted = true;
      getSchoolProfileData?.data?.obj?.legals?.forEach((item) => {
        if (!item?.accepted) {
          isAccepted = false;
          return;
        }
      });
      if (!isAccepted) {
        if (!policyExpireTime) {
          setisPolicyAccepted(isAccepted);
        } else {
          const prevTime = dayjs(policyExpireTime);
          const diff = currentDate.diff(prevTime, "hours");

          if (diff >= 1) {
            setisPolicyAccepted(isAccepted);
          }
        }
      }
    }
  }, [location, getSchoolProfileDataIsSuccess]);

  useEffect(() => {
    hasClosedPopupForCurrentPath.current = false;
  }, [location.pathname]);

  useEffect(() => {
    if (getSchoolProfileDataIsSuccess && isAuthenticated) {
      if (hasClosedPopupForCurrentPath.current) {
        return;
      }

      // Check if current URL contains any of the allowed modules
      const currentPath = location.pathname.toLowerCase();
      const shouldShowBasedOnUrl = ALLOWED_POPUP_MODULES.some((module) =>
        currentPath.includes(module.toLowerCase()),
      );

      // Check if current date is greater than due date
      const shouldShowBasedOnDate = currentDate.isAfter(dueDate);

      // Show popup if both conditions are met
      if (shouldShowBasedOnUrl && shouldShowBasedOnDate) {
        setShowDueChargePopup(true);
      } else {
        setShowDueChargePopup(false);
      }
    }
  }, [
    location.pathname,
    getSchoolProfileDataIsSuccess,
    isAuthenticated,
    currentDate,
    dueDate,
  ]);

  // Beta awareness popup — shown at most once per slot (morning / noon), so
  // twice a day per user, and never on top of the policy / payment dialogs.
  useEffect(() => {
    if (!isAuthenticated || !schoolDocId) return;
    if (!isPolicyAccepted || showDueChargePopup) return;
    if (showBetaPromo) return;

    const check = () => {
      if (!shouldShowBetaPromo(userDocId)) return;
      markBetaPromoShown(userDocId);
      setShowBetaPromo(true);
    };

    // First look is delayed so it never lands on top of a just-mounted screen;
    // after that we keep checking, so the noon showing still happens in a tab
    // that has been left open since the morning.
    const timer = setTimeout(check, 2000);
    const interval = setInterval(check, 5 * 60 * 1000);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [
    isAuthenticated,
    schoolDocId,
    userDocId,
    isPolicyAccepted,
    showDueChargePopup,
    showBetaPromo,
  ]);

  return (
    <>
      <Policies
        legals={getSchoolProfileData?.data?.obj?.legals}
        refetch={getSchoolProfileRefetch}
        open={!isPolicyAccepted}
        onClose={handleClosePolicyDialog}
      />
      <DueServiceChargePopup
        open={showDueChargePopup}
        onClose={handleCloseDueChargePopup}
      />
      {/* Mounted only while signed in — its selectors read auth state that
          is null on the login screen. */}
      {isAuthenticated && (
        <BetaPromoPopup
          open={showBetaPromo}
          userDocId={userDocId}
          onClose={() => setShowBetaPromo(false)}
        />
      )}
      <StyledWrapper id="layout">
        {isAuthenticated ? (
          isMobile ? (
            <>
              <MobileHeader setShow={setShow} />
            </>
          ) : (
            <div>
              {/* <ZohoSalesIQ /> */}
              <Header handleOnClickHamburgeMenu={handleOnClickHamburgeMenu} />
            </div>
          )
        ) : null}
        {isMobile ? (
          <>
            <SwipeableDrawer
              open={show}
              onClose={() => {
                setShow(false);
              }}
              onOpen={() => {
                setShow(true);
              }}
              anchor="left"
              PaperProps={{
                sx: {},
              }}
            >
              <MobileSidebar setShow={setShow} />
            </SwipeableDrawer>
            <Box className="w-100" sx={{ height: "100vh", overflow: "hidden" }}>
              <Box
                className="main-content"
                sx={{
                  pt: "70px",
                  px: 2,
                  overflowY: "auto",
                  overflowX: "hidden",
                  height: "100vh",
                  boxSizing: "border-box",
                }}
              >
                {getSchoolProfileData?.data?.obj?.accountStatus !== "ACTIVE" &&
                location.pathname !== `/${appRoutes.schoolProfile}` &&
                location.pathname !== `/${appRoutes.billsUsage}` &&
                !isSettingsPage &&
                getSchoolProfileDataIsSuccess ? (
                  <>
                    <Stack
                      direction={"row"}
                      justifyContent={"center"}
                      alignItems={"center"}
                      height={"100%"}
                    >
                      <DeactivateContent />
                    </Stack>
                  </>
                ) : (
                  children
                )}
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Stack
              direction="row"
              sx={{
                position: "relative",
                height: "100vh",
                overflow: "hidden",
                width: "100%",
              }}
            >
              {/* Sidebar for Desktop (>1024px) */}
              {!isMinimalLayoutPage && !isTablet && (
                <Box
                  className={`sidebar-wrapper shadow ${
                    !isSidebarToggled ? "sidebar-hidden" : ""
                  }`}
                >
                  {isAuthenticated ? (
                    <Sidebar isSidebarToggled={isSidebarToggled} />
                  ) : null}
                </Box>
              )}

              {/* Sidebar Drawer for Tablet (<=1024px, >768px) */}
              {!isMinimalLayoutPage && isTablet && !isMobile && (
                <SwipeableDrawer
                  open={isSidebarToggled}
                  onClose={() => setIsSidebarToggled(false)}
                  onOpen={() => setIsSidebarToggled(true)}
                  anchor="left"
                  PaperProps={{
                    sx: {
                      width: "201px",
                      backgroundColor: "#084c84",
                    },
                  }}
                >
                  {isAuthenticated ? <Sidebar isSidebarToggled={true} /> : null}
                </SwipeableDrawer>
              )}

              <Box
                sx={{
                  width: "100%",
                  marginLeft:
                    !isMinimalLayoutPage && isSidebarToggled && !isTablet
                      ? "201px"
                      : 0,
                  marginTop: "70px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  transition: "margin-left 0.3s ease",
                  height: "100vh",
                  overflow: "hidden",
                  paddingTop:
                    dueDate.diff(currentDate, "day") <= 5 ||
                    currentDate.isSame(dueDate)
                      ? "45px"
                      : "0",
                }}
              >
                {(dueDate.diff(currentDate, "day") <= 5 ||
                  currentDate.isSame(dueDate)) && (
                  <MarqueText>
                    <Typography className="marque_text">
                      Kindly settle your service charge prior to the date{" "}
                      {dueDate.format("DD MMM, YYYY")} to ensure uninterrupted
                      service operation.-{" "}
                      <Link to={`/${appRoutes.schoolProfile}?tab=transaction`}>
                        See More
                      </Link>
                    </Typography>
                  </MarqueText>
                )}
                {/* <Box
                  className="main-content px-3 for-scroll"
                  sx={{
                    paddingTop: "20px",
                    height: "calc(100vh - 95px)",
                    overflowY: "auto",
                    // pb: bottomStripHeight,
                  }}
                >
                  {(getSchoolProfileData?.data?.obj?.accountStatus !==
                    "ACTIVE" ||
                    !isPolicyAccepted) &&
                  location.pathname !== `/${appRoutes.schoolProfile}` &&
                  getSchoolProfileDataIsSuccess ? (
                    <>
                      <Stack
                        direction={"row"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        height={"100%"}
                      >
                        {isPolicyAccepted ? (
                          <DeactivateContent />
                        ) : (
                          <Policies
                            legals={getSchoolProfileData?.data?.obj?.legals}
                            refetch={getSchoolProfileRefetch}
                          />
                        )}
                      </Stack>
                    </>
                  ) : (
                    children
                  )}
                </Box> */}
                <Box
                  className="main-content for-scroll"
                  sx={{
                    paddingTop: isMinimalLayoutPage ? "0px" : "20px",
                    paddingX: isMinimalLayoutPage
                      ? "0px"
                      : { xs: "16px", sm: "20px", md: "24px" },
                    paddingBottom: "90px", // Extra padding to prevent content from hiding behind footer
                    height: isMinimalLayoutPage
                      ? `calc(100vh - 70px - ${bottomStripHeight})`
                      : `calc(100vh - 70px - 20px - ${bottomStripHeight})`,
                    overflowY: "auto",
                    overflowX: "hidden",
                    flex: "1 1 auto",
                    boxSizing: "border-box",
                  }}
                >
                  {getSchoolProfileData?.data?.obj?.accountStatus !==
                    "ACTIVE" &&
                  location.pathname !== `/${appRoutes.schoolProfile}` &&
                  location.pathname !== `/${appRoutes.billsUsage}` &&
                  !isSettingsPage &&
                  getSchoolProfileDataIsSuccess ? (
                    <>
                      <Stack
                        direction={"row"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        height={"100%"}
                      >
                        <DeactivateContent />
                      </Stack>
                    </>
                  ) : (
                    children
                  )}
                </Box>
                {/* manage height if bottom strip */}
                <Box sx={{ height: bottomStripHeight, flexShrink: 0 }} />
              </Box>
            </Stack>
            {/* bottom Strip - show on all pages including settings */}
            <FooterBar
              sx={{
                width: "100%",
                height: bottomStripHeight,
                position: "fixed",
                bottom: 0,
                zIndex: 1000,
              }}
            >
              <Stack
                direction="row"
                justifyContent={"space-between"}
                alignItems={"center"}
                height="100%"
                sx={{
                  px: { xs: 2, sm: 3, md: 4 },
                  flexWrap: { xs: "wrap", md: "nowrap" },
                }}
              >
                {/* Social Media Links */}
                <Stack
                  direction={"row"}
                  gap={{ xs: 1, sm: 1.5, md: 2 }}
                  alignItems="center"
                  className="social-links"
                  sx={{
                    display: { xs: "none", sm: "flex" },
                  }}
                >
                  <a
                    href="https://www.linkedin.com/company/ekalsutraofficial/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <LinkedInIcon sx={{ fontSize: "18px" }} />
                    <span>LinkedIn</span>
                  </a>
                  <a
                    href="https://www.facebook.com/ekalsutrahelp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <FacebookIcon sx={{ fontSize: "18px" }} />
                    <span>Facebook</span>
                  </a>
                  <a
                    href="https://www.instagram.com/ekalsutra/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <InstagramIcon sx={{ fontSize: "18px" }} />
                    <span>Instagram</span>
                  </a>
                  <a
                    href="https://www.youtube.com/@ekalsutra"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <YouTubeIcon sx={{ fontSize: "18px" }} />
                    <span>YouTube</span>
                  </a>
                  <a
                    href="https://twitter.com/EkalSutra"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <TwitterIcon sx={{ fontSize: "18px" }} />
                    <span>X/Twitter</span>
                  </a>
                </Stack>

                {/* Policy Links */}
                <Stack
                  direction="row"
                  gap={{ xs: 1, sm: 1.5, md: 2 }}
                  alignItems="center"
                  className="policy-links"
                  sx={{
                    fontSize: { xs: "11px", sm: "12px" },
                  }}
                >
                  <Link
                    to="/schools/term-and-conditions"
                    className="footer-link"
                  >
                    <Typography className="footer-link-text">TnC</Typography>
                  </Link>

                  <Link to="/schools/refund-policy" className="footer-link">
                    <Typography className="footer-link-text">
                      Refund Policy
                    </Typography>
                  </Link>

                  <Link to="/schools/privacy-policy" className="footer-link">
                    <Typography className="footer-link-text">
                      Privacy Policy
                    </Typography>
                  </Link>

                  <Link to="/schools/about-school" className="footer-link">
                    <BuildingGear className="footer-icon" />
                    <Typography className="footer-link-text">
                      About School
                    </Typography>
                  </Link>

                  <Link to="/schools/school-profile" className="footer-link">
                    <Building className="footer-icon" />
                    <Typography className="footer-link-text">
                      School Profile
                    </Typography>
                  </Link>
                </Stack>
              </Stack>
            </FooterBar>
          </>
        )}
      </StyledWrapper>
    </>
  );
};

export default Layout;

const FooterBar = styled(Box)`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-top: 1px solid rgba(8, 76, 132, 0.08);
  box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.03);
  backdrop-filter: blur(10px);

  @media print {
    display: none !important;
  }

  .social-links {
    a.social-link {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      text-decoration: none;
      color: #5a7184;
      font-size: 11px;
      font-weight: 600;
      padding: 5px 10px;
      border-radius: 6px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      background: transparent;
      border: 1px solid transparent;

      svg {
        font-size: 16px;
        transition: all 0.3s ease;
      }

      &:hover {
        background: rgba(8, 76, 132, 0.05);
        border-color: rgba(8, 76, 132, 0.1);
        color: #084c84;
        transform: translateY(-1px);

        svg {
          transform: scale(1.15);
        }
      }
    }
  }

  .policy-links {
    a.footer-link {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      text-decoration: none;
      color: #5a7184;
      padding: 5px 10px;
      border-radius: 6px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      background: transparent;
      border: 1px solid transparent;
      position: relative;

      .footer-link-text {
        font-size: 11px;
        font-weight: 600;
        color: inherit;
        transition: color 0.3s ease;
      }

      .footer-icon {
        font-size: 15px;
        opacity: 0.75;
        transition: all 0.3s ease;
      }

      &:hover {
        background: rgba(8, 76, 132, 0.05);
        border-color: rgba(8, 76, 132, 0.1);
        color: #084c84;

        .footer-icon {
          opacity: 1;
          transform: scale(1.1);
        }
      }

      &:not(:last-child)::after {
        content: "";
        position: absolute;
        right: -8px;
        top: 50%;
        transform: translateY(-50%);
        width: 1px;
        height: 14px;
        background: rgba(8, 76, 132, 0.15);
      }
    }
  }
`;

const StyledWrapper = styled.div`
  width: 100%;
  height: 100vh;
  overflow: hidden;

  /* background-color: #fbfbff; */
  background-color: #f0f2f5;

  @media print {
    height: auto;
    overflow: visible;

    /* Remove all overflow and height constraints for child elements */
    & > *,
    & * {
      overflow: visible !important;
      height: auto !important;
      max-height: none !important;
    }
  }
  .bg-blue {
    background: blue;
  }
  ul {
    padding: 0px;
  }
  .sidebar-wrapper {
    background: linear-gradient(180deg, #084c84 0%, #063d6b 100%);
    margin: 0;
    height: 100vh;
    position: fixed;
    left: 0;
    top: 0;
    z-index: 999;
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 201px;

    ${screen.medium.standard`
      width: 56px;
    `};

    &.sidebar-hidden {
      transform: translateX(-100%);
      box-shadow: none;
    }
  }
`;
const marqueKeyFrams = keyframes`
 0% { transform: translate(0, 0); }
    100% { transform: translate(-100%, 0); }
`;
const MarqueText = styled.div`
  margin: 0 auto;
  white-space: nowrap;
  overflow: hidden;
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 999;
  pointer-events: none;
  user-select: none;
  background: rgba(255, 235, 235, 0.95);
  padding: 6px 0;
  border-bottom: 1px solid rgba(244, 67, 54, 0.2);

  @media print {
    display: none !important;
  }

  .marque_text {
    display: inline-block;
    padding-left: 100%;
    color: red;
    font-weight: 600;
    font-size: 14px;
    animation: ${marqueKeyFrams} 20s linear infinite;
    cursor: default;
    pointer-events: none;

    a {
      pointer-events: auto;
      text-decoration: underline;
      color: blue;
      cursor: pointer;
    }
  }
`;
